import {
  type EntitySummary,
  GRAPH_STATUSES,
  itemDateLabel,
  MAP_KIND_LABELS,
  MAP_KINDS,
  type MapEvent,
  type MapFilter,
  type MapKind,
  mapFeatures,
} from '@hoopoe/shared';
import { createFileRoute, Link } from '@tanstack/react-router';
import { Loader2, Lock, Map as MapIcon, MapPin, X } from 'lucide-react';
import { useId, useMemo, useState } from 'react';
import { ColorLegend } from '../components/colors/color-by';
import { DataText } from '../components/data/data-text';
import { EntityLink, REVIEW_STATUS_LABELS, StatusBadge } from '../components/entities/entity-link';
import { Section } from '../components/graph/panels';
import { EmptyState } from '../components/guidance/empty-state';
import { FieldHelp } from '../components/guidance/field-help';
import { Tip } from '../components/guidance/tip';
import { useScreenTour } from '../components/guidance/tour';
import { PageHeader } from '../components/layout/app-shell';
import { LocateDialog } from '../components/map/locate-dialog';
import { MapCanvas, type MapMark } from '../components/map/map-canvas';
import { Button } from '../components/ui/button';
import { ApiError } from '../lib/api';
import { useStyleOf } from '../lib/colors';
import { shapeOf } from '../lib/entities';
import { useThemeColors } from '../lib/graph';
import { useMapData } from '../lib/map';
import { useSchema } from '../lib/schema';
import { useCan } from '../lib/session';

// Map view (PLAN §9, §12.7 screen 7; task 30): Places with coordinates and the Events held there, clustered where
// they overlap, over a grey OpenStreetMap basemap served from Hoopoe's own storage. Colors, shapes and outlines mean
// what they mean everywhere. Places without coordinates are listed with "Locate": the GeoNames gazetteer (kept in
// Hoopoe's database) proposes a match, with AI picking between places of the same name from the place's context.

export const Route = createFileRoute('/map')({ component: MapPage });

const TOUR = {
  id: 'map',
  steps: [
    {
      target: '[data-tour="map-canvas"]',
      title: 'Places and events',
      body: 'Pins are places, diamonds the events held there. Numbers are groups of marks close together: click one to zoom in or open the place.',
      learnMore: 'map',
    },
    {
      target: '[data-tour="map-filters"]',
      title: 'Narrow it down',
      body: 'Show places, events or both, pick review statuses, and keep only events from a range of years.',
    },
    {
      target: '[data-tour="map-locate"]',
      title: 'Put places on the map',
      body: 'Places without coordinates are listed here. Locate one: Hoopoe suggests the right town from its gazetteer, you confirm.',
      learnMore: 'map',
    },
  ],
};

const year = (v: string) => (/^\d{4}$/.test(v) ? Number(v) : null);

function MapPage() {
  useScreenTour(TOUR);
  const { data: snap } = useSchema();
  const q = useMapData();
  const canEdit = useCan('content.write');
  const styleOf = useStyleOf(snap);
  const colors = useThemeColors();
  const dark = useMemo(() => {
    void colors;
    return typeof document !== 'undefined' && getComputedStyle(document.documentElement).colorScheme === 'dark';
  }, [colors]);
  const [kinds, setKinds] = useState<MapKind[]>([...MAP_KINDS]);
  const [statuses, setStatuses] = useState<MapFilter['statuses']>([...GRAPH_STATUSES]);
  const [fromYear, setFromYear] = useState('');
  const [toYear, setToYear] = useState('');
  const [selected, setSelected] = useState<string | null>(null);
  const [focus, setFocus] = useState<{ lat: number; lng: number; key: number } | null>(null);
  const [locating, setLocating] = useState<EntitySummary | null>(null);
  const [now] = useState(() => Date.now());
  const uid = { from: useId(), to: useId() };

  const data = q.data;
  const entities = useMemo(() => new Map((data?.entities ?? []).map(e => [e.id, e])), [data]);
  const filter: MapFilter = useMemo(
    () => ({ kinds, statuses, fromYear: year(fromYear), toYear: year(toYear) }),
    [kinds, statuses, fromYear, toYear],
  );
  const features = useMemo(() => (data ? mapFeatures(data, filter, now) : []), [data, filter, now]);
  const marks: MapMark[] = useMemo(
    () =>
      snap
        ? features.flatMap(f => {
            const e = entities.get(f.entityId);
            if (!e) return [];
            const style = styleOf(e);
            return [
              {
                ...f,
                look: {
                  kind: shapeOf(snap, e.typeId, e.kind),
                  status: f.status,
                  fill: colors.entity(style.colorSlot),
                  stroke: colors.foreground,
                  fade: style.fade,
                  ring: style.ring ? (style.ringSlot ? colors.entity(style.ringSlot) : colors.foreground) : null,
                  confidential: f.sensitivity === 'confidential',
                  warning: colors.warning,
                  badge: colors.surface,
                },
              },
            ];
          })
        : [],
    [features, entities, snap, styleOf, colors],
  );
  // The places shown, each with its events in view (the accessible list beside the map).
  const byPlace = useMemo(() => {
    const out = new Map<string, MapEvent[]>();
    const eventsById = new Map((data?.events ?? []).map(e => [e.id, e]));
    for (const f of features) {
      if (!out.has(f.placeId)) out.set(f.placeId, []);
      if (f.kind === 'event') {
        const ev = eventsById.get(f.id);
        if (ev) out.get(f.placeId)?.push(ev);
      }
    }
    return out;
  }, [features, data]);
  const legendStyles = useMemo(
    () =>
      [...new Set(features.map(f => f.entityId))]
        .map(id => entities.get(id))
        .filter((e): e is EntitySummary => !!e)
        .map(e => styleOf(e)),
    [features, entities, styleOf],
  );

  const selectedPlaceId = selected ? (features.find(f => f.id === selected)?.placeId ?? (entities.has(selected) ? selected : null)) : null;
  const filtersOn = kinds.length < MAP_KINDS.length || (statuses?.length ?? 0) < GRAPH_STATUSES.length || !!fromYear || !!toYear;
  const toggle = <T,>(list: T[], v: T) => (list.includes(v) ? list.filter(x => x !== v) : [...list, v]);
  const placeCount = features.filter(f => f.kind === 'place').length;
  const eventCount = features.filter(f => f.kind === 'event').length;
  const pick = (placeId: string) => {
    setSelected(placeId);
    const p = data?.places.find(x => x.id === placeId);
    if (p) setFocus({ lat: p.lat, lng: p.lng, key: Date.now() });
  };

  if (!snap) return <PageHeader title="Map" />;

  return (
    <>
      <PageHeader
        title="Map"
        description="Places with coordinates and the events held there. Colors, shapes and outlines mean the same as in every list."
      />
      <Tip id="map" learnMore="map">
        A place appears once it has coordinates. Use Locate on a place below the map: Hoopoe suggests the right town from its own gazetteer,
        and you confirm.
      </Tip>

      <fieldset className="mt-4 flex flex-wrap items-end gap-x-6 gap-y-3 rounded-lg border bg-surface p-3" data-tour="map-filters">
        <legend className="sr-only">Filters</legend>
        <fieldset className="space-y-1">
          <legend className="block text-xs font-medium">Show</legend>
          <div className="flex flex-wrap gap-3 text-sm">
            {MAP_KINDS.map(k => (
              <label key={k} className="flex items-center gap-1.5">
                <input type="checkbox" checked={kinds.includes(k)} onChange={() => setKinds(v => toggle(v, k))} />
                {MAP_KIND_LABELS[k]}
              </label>
            ))}
          </div>
        </fieldset>
        <fieldset className="space-y-1">
          <legend className="block text-xs font-medium">Review status</legend>
          <div className="flex flex-wrap gap-3 text-sm">
            {GRAPH_STATUSES.map(s => (
              <label key={s} className="flex items-center gap-1.5">
                <input type="checkbox" checked={statuses?.includes(s) ?? false} onChange={() => setStatuses(v => toggle(v ?? [], s))} />
                {REVIEW_STATUS_LABELS[s]}
              </label>
            ))}
          </div>
        </fieldset>
        <div className="flex items-end gap-2">
          <div className="space-y-1">
            <label htmlFor={uid.from} className="block text-xs font-medium">
              Events from year
            </label>
            <input
              id={uid.from}
              inputMode="numeric"
              value={fromYear}
              onChange={e => setFromYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 2015"
              className="h-9 w-24 rounded-md border border-input bg-surface px-2 text-sm tabular"
            />
          </div>
          <div className="space-y-1">
            <label htmlFor={uid.to} className="block text-xs font-medium">
              To year
            </label>
            <input
              id={uid.to}
              inputMode="numeric"
              value={toYear}
              onChange={e => setToYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
              placeholder="e.g. 2024"
              className="h-9 w-24 rounded-md border border-input bg-surface px-2 text-sm tabular"
            />
          </div>
          <FieldHelp
            label="Years"
            help="Keep only events that overlap these years; events without a date are hidden while a year is set. Places always stay."
            example="2019 to 2024"
            learnMore="map"
          />
        </div>
        {filtersOn && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => {
              setKinds([...MAP_KINDS]);
              setStatuses([...GRAPH_STATUSES]);
              setFromYear('');
              setToYear('');
            }}
          >
            Reset filters
          </Button>
        )}
      </fieldset>

      <div aria-live="polite" className="mt-3 min-h-5 text-sm">
        {q.isFetching && !data ? (
          <span className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="size-4 animate-spin" aria-hidden="true" /> Loading the map…
          </span>
        ) : q.isError ? (
          <span role="alert" className="text-danger-foreground">
            {q.error instanceof ApiError ? q.error.message : 'The map could not be loaded. Try again.'}
          </span>
        ) : data ? (
          <span className="text-muted-foreground">
            {placeCount} {placeCount === 1 ? 'place' : 'places'} and {eventCount} {eventCount === 1 ? 'event' : 'events'} on the map
            {data.truncated && ' (some places were left out: there were too many)'}
            {!data.basemap && ' · the background map is not installed here yet, so marks show on a plain background'}.
          </span>
        ) : null}
      </div>

      <div className="mt-1 grid gap-4 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <div className="h-[60vh] min-h-80 overflow-hidden rounded-lg border bg-surface" data-tour="map-canvas">
          {data && (
            <MapCanvas
              marks={marks}
              selected={selected}
              onSelect={setSelected}
              colors={colors}
              dark={dark}
              basemap={data.basemap}
              focus={focus}
              label={`Map: ${placeCount} ${placeCount === 1 ? 'place' : 'places'}, ${eventCount} ${eventCount === 1 ? 'event' : 'events'}`}
            />
          )}
        </div>
        <div className="space-y-4">
          {selectedPlaceId && entities.get(selectedPlaceId) && (
            <PlaceDetails
              place={entities.get(selectedPlaceId) as EntitySummary}
              position={data?.places.find(p => p.id === selectedPlaceId)}
              events={byPlace.get(selectedPlaceId) ?? []}
              entities={entities}
              selectedEvent={selected && selected !== selectedPlaceId ? selected : null}
              onClose={() => setSelected(null)}
              onLocate={canEdit ? () => setLocating(entities.get(selectedPlaceId) ?? null) : undefined}
            />
          )}
          <Section title="Legend">
            {legendStyles.length > 0 && <ColorLegend styles={legendStyles} />}
            <p className="mt-2 text-xs text-muted-foreground">
              Pin = place · ◆ event, around its place’s pin · circle with a number = several marks close together (zoom in to see them).
              Solid outline = verified, dashed = unreviewed, double with ⚠ = disputed · <Lock className="inline size-3" aria-label="lock" />{' '}
              = confidential.
            </p>
          </Section>
        </div>
      </div>

      {data && (
        <div className="mt-4 grid gap-4 lg:grid-cols-2">
          <Section title={`On the map (${byPlace.size})`}>
            {byPlace.size === 0 ? (
              <EmptyState icon={MapIcon} title={filtersOn ? 'Nothing matches the filters' : 'No place on the map yet'} learnMore="map">
                {filtersOn
                  ? 'Reset the filters to see every place.'
                  : 'Places appear here once they have coordinates. Locate one from the list of places to locate.'}
              </EmptyState>
            ) : (
              <ul aria-label="Places on the map" className="max-h-96 space-y-1 overflow-y-auto text-sm">
                {[...byPlace.entries()].map(([placeId, events]) => {
                  const p = entities.get(placeId);
                  if (!p) return null;
                  return (
                    <li key={placeId}>
                      <button
                        type="button"
                        aria-pressed={selectedPlaceId === placeId}
                        onClick={() => pick(placeId)}
                        className="flex w-full min-w-0 items-center gap-2 rounded px-1 py-0.5 text-start hover:bg-muted aria-pressed:bg-muted"
                      >
                        <MapPin className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
                        <DataText className="min-w-0 truncate font-medium">{p.displayName}</DataText>
                        <span className="ms-auto shrink-0 text-xs text-muted-foreground">
                          {events.length} {events.length === 1 ? 'event' : 'events'}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Section>
          <div data-tour="map-locate">
            <Section title={`Places to locate (${data.unlocatedTotal})`}>
              <p className="mb-2 text-xs text-muted-foreground">
                These places have no coordinates yet.
                {data.eventsAtUnlocated > 0 &&
                  ` ${data.eventsAtUnlocated} ${data.eventsAtUnlocated === 1 ? 'event is' : 'events are'} waiting for one of them.`}
                {data.eventsWithoutPlace > 0 &&
                  ` ${data.eventsWithoutPlace} ${data.eventsWithoutPlace === 1 ? 'event has' : 'events have'} no place at all: add one on the event’s profile.`}
              </p>
              {data.unlocated.length === 0 ? (
                <p className="text-sm text-muted-foreground">Every place is on the map.</p>
              ) : (
                <ul aria-label="Places to locate" className="max-h-96 space-y-1 overflow-y-auto text-sm">
                  {data.unlocated.map(p => (
                    <li key={p.id} className="flex min-w-0 items-center gap-2">
                      <EntityLink schema={snap} entity={p} className="min-w-0 flex-1 truncate" />
                      {canEdit && (
                        <Button size="sm" variant="outline" onClick={() => setLocating(p)} aria-label={`Locate ${p.displayName}`}>
                          <MapPin aria-hidden="true" /> Locate
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Section>
          </div>
        </div>
      )}

      {data && !data.places.length && !data.unlocated.length && (
        <div className="mt-4">
          <EmptyState icon={MapIcon} title="No places yet" learnMore="map">
            Places come from uploads and profiles (an event’s Place, an organization’s Headquarters). Add one from the directory.
          </EmptyState>
        </div>
      )}

      {locating && (
        <LocateDialog
          place={locating}
          open={!!locating}
          onOpenChange={open => !open && setLocating(null)}
          onLocated={pos => {
            setSelected(locating.id);
            setFocus({ ...pos, key: Date.now() });
          }}
        />
      )}
    </>
  );
}

function PlaceDetails({
  place,
  position,
  events,
  entities,
  selectedEvent,
  onClose,
  onLocate,
}: {
  place: EntitySummary;
  position: { lat: number; lng: number; status: EntitySummary['reviewStatus'] } | undefined;
  events: MapEvent[];
  entities: Map<string, EntitySummary>;
  selectedEvent: string | null;
  onClose(): void;
  onLocate?: () => void;
}) {
  const { data: snap } = useSchema();
  if (!snap) return null;
  return (
    <Section
      title="Place"
      actions={
        <Button size="sm" variant="ghost" onClick={onClose} aria-label="Close details">
          <X aria-hidden="true" />
        </Button>
      }
    >
      <div data-testid="map-details" className="space-y-3 text-sm">
        <EntityLink schema={snap} entity={place} />
        {position && (
          <p className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground tabular">
            {position.lat.toFixed(5)}, {position.lng.toFixed(5)} <StatusBadge status={position.status} />
          </p>
        )}
        {events.length > 0 ? (
          <ul aria-label={`Events at ${place.displayName}`} className="space-y-1.5">
            {events.map(ev => {
              const e = entities.get(ev.eventId);
              if (!e) return null;
              return (
                <li key={ev.id} className={selectedEvent === ev.id ? 'rounded bg-muted px-1' : 'px-1'}>
                  <EntityLink schema={snap} entity={e} />
                  <span className="block text-xs text-muted-foreground">
                    {itemDateLabel(ev)} · {REVIEW_STATUS_LABELS[ev.status]}
                    {ev.sensitivity === 'confidential' && ' · confidential'}
                  </span>
                </li>
              );
            })}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">No event at this place matches the filters.</p>
        )}
        <div className="flex flex-wrap gap-3">
          <Link to="/entity/$id" params={{ id: place.id }} className="font-medium underline-offset-2 hover:underline">
            Open profile
          </Link>
          {onLocate && (
            <button type="button" onClick={onLocate} className="font-medium underline-offset-2 hover:underline">
              Move the pin
            </button>
          )}
        </div>
      </div>
    </Section>
  );
}
